import {
  HttpClient,
  HttpContext,
  HttpContextToken,
  HttpErrorResponse,
  HttpParams,
} from '@angular/common/http';
import { CUSTOMER_CONFIG } from 'app/core/customer/customer.config';
import { environment } from 'environments/environment';
import {
  Observable,
  catchError,
  throwError,
  timeout,
} from 'rxjs';
import { AuthUtils } from 'app/core/auth/auth.utils';

/** External LOT requests must not receive the application's bearer token. */
export const EXTERNAL_LOT_API_REQUEST = new HttpContextToken<boolean>(() => false);

export function generateCteLot(
  http: HttpClient,
  legacyUrl: string,
  options: {
    responseType: 'text';
    params?: HttpParams | Record<string, string | number | boolean>;
  },
): Observable<string> {
  if (CUSTOMER_CONFIG.customerId !== 'ecsg') {
    return http.get(legacyUrl, options);
  }

  const config = environment.ecsgLotApi;

  if (!config?.apiKey?.trim()) {
    console.error(
      '[ECSG LOT API] Missing API key. Set ECSG_LOT_API_KEY in .env.ecsg.local or the build environment, then restart with npm run start:ecsg or rebuild with npm run build:ecsg.',
    );
    return throwError(
      () => new Error('ECSG_LOT_API_KEY is not configured.'),
    );
  }

  const accessToken = localStorage.getItem('accessToken') ?? '';

  if (!accessToken || AuthUtils.isTokenExpired(accessToken)) {
    return throwError(
      () =>
        new Error(
          'Your session has expired. Please sign in again before generating a LOT.',
        ),
    );
  }

  const username = getAuthenticatedUsername();

return http
  .post(config.url, null, {
    responseType: 'text',
    headers: {
      'x-api-key': config.apiKey,
      Accept: 'text/plain',
    },
    params: {
      user: username,
    },
    context: new HttpContext().set(EXTERNAL_LOT_API_REQUEST, true),
  })
  .pipe(
    timeout(15000),
    catchError((error) => {
      if (error instanceof HttpErrorResponse && [401, 403].includes(error.status)) {
        console.error(
          `[ECSG LOT API] Authentication rejected (HTTP ${error.status}). The API key may be wrong, expired, or unauthorized. Check ECSG_LOT_API_KEY in .env.ecsg.local or the build environment, then restart with npm run start:ecsg or rebuild with npm run build:ecsg.`,
        );
      }

      if (
        error?.name === 'TimeoutError' ||
        (error instanceof HttpErrorResponse && error.status === 0)
      ) {
        return throwError(
          () =>
            new Error(
              'LOT allocation could not be confirmed. Reconcile the request before generating another LOT.',
            ),
        );
      }

      return throwError(() => error);
    }),
  );
}

function getAuthenticatedUsername(): string {
  const sessionUsername = String(
    sessionStorage.getItem('username') ?? '',
  ).trim();

  if (sessionUsername) {
    return sessionUsername;
  }

  try {
    const authData = JSON.parse(
      localStorage.getItem('userData') ?? '{}',
    );

    const username = String(authData?.username ?? '').trim();

    if (username) {
      return username;
    }
  } catch {
    // handled below
  }

  throw new Error(
    'An authenticated username is required to generate a LOT.',
  );
}
